import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer, huidigeTaal } from '../i18n.js';
import { St } from '../components/Status.jsx';
import Vlag from '../components/Vlag.jsx';
import Gemaskeerd from '../components/Gemaskeerd.jsx';
import Lijsten from '../components/Lijsten.jsx';
import { landnaam } from '../components/Wereldkaart.jsx';
import Stapelstaven from '../components/Stapelstaven.jsx';
import Heatmap from '../components/Heatmap.jsx';
import Balken from '../components/Balken.jsx';
import Tunnels from '../components/Tunnels.jsx';
import AnyConnectVersies, { PLATFORM } from '../components/AnyConnectVersies.jsx';

/**
 * Het VPN-overzicht (docs/besluiten/vpn-overzicht.md): de mislukte logins per adres met wie het waarschijnlijk
 * was, en de sessies. Voluit, want alleen voor een aangemelde medewerker; het SOC-scherm toont initialen.
 */
const PERIODE = { '24h': '24 uur', '7d': '7 dagen', '30d': '30 dagen' };
const DAGEN = { '24h': 1, '7d': 7, '30d': 30 };

const duur = (s) => {
  if (s === null || s === undefined) return null;
  const u = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return u ? t('{u} u {m} min', { u, m }) : m ? t('{n} min', { n: m }) : t('{n} s', { n: s });
};
const tussen = (van, tot) => Math.max(0, Math.round((new Date(tot) - new Date(van)) / 1000));

function Plek({ p }) {
  return (
    <>
      <Gemaskeerd veld={p.ip} mono /><br />
      <span className="muted" style={{ fontSize: 12.5 }}>{p.internal ? t('intern') : <><Vlag cc={p.cc} />{p.cc ?? '—'}</>}
        {p.org ? ` · ${p.org}` : ''}</span>
      {p.hosting && <> <St soort="serious">{t('hostingprovider')}</St></>}
      <Lijsten lijsten={p.lists} />
    </>
  );
}

function Wie({ p }) {
  if (p.match === 'daarna') return <><b className="mono"><Gemaskeerd veld={p.user} /></b><br /><span className="muted">{t('logde {tijd} later in', { tijd: duur(tussen(p.lastAt, p.matchAt)) })}</span></>;
  if (p.match === 'ervoor') return <><b className="mono"><Gemaskeerd veld={p.user} /></b><br /><span className="muted">{t('was kort ervoor ingelogd')}</span></>;
  if (p.match === 'bekend') return <><span className="mono"><Gemaskeerd veld={p.user} /></span><br /><span className="muted">{t('adres bekend van deze gebruiker, laatst {wanneer}', { wanneer: wanneer(p.matchAt) })}</span></>;
  return <St soort="warning">{t('niemand bekend, niet ingelogd')}</St>;
}

export default function Vpn({ klant }) {
  /*
   * Twee tabs (Bernhard, 2026-10-26): de clients (remote access, AnyConnect) en de site-to-site-tunnels. De tab staat in de
   * adresbalk (?tab=s2s). In de tab Site-to-site een teller, rood als er een tunnel neer is: dan zie je het ook vanaf
   * Clients.
   */
  const [zoek, zetZoek] = useSearchParams();
  const tab = zoek.get('tab') === 's2s' ? 's2s' : 'clients';
  const [range, zetRange] = useState('24h');
  const q = useQuery({ queryKey: ['vpn', klant, range], queryFn: () => api.vpn(klant, range), refetchInterval: 30_000, enabled: tab === 'clients' });
  const s2s = useQuery({ queryKey: ['tunnels', klant], queryFn: () => api.tunnels(klant), refetchInterval: 60_000 });
  const tunnels = s2s.data?.data.tunnels ?? [];
  const neer = tunnels.filter((x) => x.state !== 'TUNNEL_UP').length;
  const d = q.data?.data;
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>;

  return (
    <>
      <div className="page-head"><div>
        <h1>{t('VPN')}</h1>
        <p className="sub">{tab === 's2s' ? t('De site-to-site-tunnels: hun stand uit de FMC, en wat de firewall erover logt.')
          : t('Wie er via remote access inlogt. De firewall verbergt de naam bij een mislukte login; ADTS ZERO zoekt wie er vanaf hetzelfde adres wél inlogde.')}</p>
      </div></div>
      <div className="tabs" role="tablist" aria-label={t('Onderdelen van VPN')}>
        <button type="button" role="tab" aria-selected={tab === 'clients'} className={tab === 'clients' ? 'aan' : undefined} onClick={() => zetZoek({})}>{t('Clients')}</button>
        <button type="button" role="tab" aria-selected={tab === 's2s'} className={tab === 's2s' ? 'aan' : undefined} onClick={() => zetZoek({ tab: 's2s' })}>
          {t('Site-to-site')}{tunnels.length > 0 && <span className={`tel ${neer ? 'rood' : 'grijs'}`}>{neer ? t('{n} neer', { n: neer }) : tunnels.length}</span>}</button>
      </div>
      {tab === 's2s' ? <Tunnels klant={klant} /> : <>
      <div className="filters">
        {Object.entries(PERIODE).map(([k, l]) => (
          <button key={k} type="button" className="chip" aria-pressed={range === k} onClick={() => zetRange(k)}>{t(l)}</button>))}
      </div>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : (
        <>
          <Logins klant={klant} dagen={DAGEN[range]} />
          <div className="tiles">
            {tegel(getal(d.totals.failed), t('mislukte logins'))}
            {tegel(getal(d.totals.unknown), t('adressen zonder bekende gebruiker'), d.totals.unknown ? 'warn' : '')}
            {tegel(getal(d.totals.sessions), t('sessies'))}
            {tegel(getal(d.totals.users), t('gebruikers'))}
          </div>

          <section className="card" style={{ marginBottom: 18 }}>
            <header><h2>{t('Mislukte logins')}</h2><span className="meta">{t('per adres, nieuwste bovenaan')}</span></header>
            {d.failed.length ? (
              <table>
                <thead><tr><th>{t('Laatste poging')}</th><th>{t('Adres')}</th><th>{t('Pogingen')}</th><th>{t('Waarschijnlijk')}</th><th className="hide-sm">{t('Firewall')}</th></tr></thead>
                <tbody>{d.failed.map((p, i) => (
                  <tr key={i}>
                    <td className="mono">{wanneer(p.lastAt)}</td>
                    <td><Plek p={p} /></td>
                    <td className="mono">{getal(p.n)}</td>
                    <td><Wie p={p} /></td>
                    <td className="hide-sm muted">{p.source ?? '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="empty">{t('Geen mislukte logins in deze periode.')}</p>}
            <p className="noot">{t('Waarschijnlijk: wie binnen 15 minuten vanaf hetzelfde adres wél inlogde, anders wie er in de 30 dagen ervoor vandaan kwam.')}</p>
          </section>

          <section className="card">
            <header><h2>{t('Sessies')}</h2><span className="meta">{t('nieuwste bovenaan')}</span></header>
            {d.sessions.length ? (
              <table>
                <thead><tr><th>{t('Begin')}</th><th>{t('Gebruiker')}</th><th>{t('Adres')}</th><th>{t('Duur')}</th><th className="hide-sm">{t('Firewall')}</th></tr></thead>
                <tbody>{d.sessions.map((s, i) => (
                  <tr key={i}>
                    <td className="mono">{wanneer(s.startedAt)}</td>
                    <td className="mono"><Gemaskeerd veld={s.user} mono /></td>
                    <td><Plek p={s} /></td>
                    <td>{s.endedAt ? <>{duur(s.seconds) ?? '—'}<br /><span className="muted" style={{ fontSize: 12.5 }}>{s.reason ?? ''}</span></>
                      : <St soort="good">{t('actief of onbekend')}</St>}</td>
                    <td className="hide-sm muted">{s.source ?? '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="empty">{t('Geen sessies in deze periode.')}</p>}
          </section>
        </>
      )}
      <AnyConnect klant={klant} />
      </>}
    </>
  );
}

/**
 * Logins in de tijd (docs/besluiten/meer-grafieken.md): geslaagd (het begin van een sessie) en mislukt per uur of dag,
 * het ritme per dag van de week en uur, de landen, en welke AnyConnect-versies gebruikers draaien. Geslaagd in
 * ADTS-cyaan, mislukt in hoog: een mislukte login is wat aandacht vraagt.
 */
function Logins({ klant, dagen }) {
  const [soort, zetSoort] = useState('mislukt');
  const q = useQuery({ queryKey: ['grafiek', 'vpn', klant, dagen], queryFn: () => api.grafiek('vpn', klant, dagen), refetchInterval: 60_000 });
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const d = q.data.data;
  const taal = huidigeTaal();
  const reeksen = [
    { sleutel: 'gelukt', label: t('geslaagd'), kleur: 'var(--cyan-ink)' },
    { sleutel: 'mislukt', label: t('mislukt'), kleur: 'var(--st-serious)' },
  ];
  const land = (cc) => (cc === 'intern' ? t('Intern') : cc === 'onbekend' ? t('Onbekend') : landnaam(cc, taal) ?? cc);
  return (
    <>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{{ hour: t('Logins per uur'), '6 hours': t('Logins per zes uur') }[d.per] ?? t('Logins per dag')}</h2>
          <span className="meta legenda">{reeksen.map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
        <Stapelstaven punten={d.tijd} reeksen={reeksen} perUur={d.per !== 'day'} label={t('VPN-logins')} />
      </section>
      <div className="grid-2" style={{ marginBottom: 18 }}>
        <section className="card">
          <header><h2>{t('Wanneer')}</h2>
            <span className="periodes">
              <button type="button" className="chip" aria-pressed={soort === 'mislukt'} onClick={() => zetSoort('mislukt')}>{t('Mislukt')}</button>
              <button type="button" className="chip" aria-pressed={soort === 'gelukt'} onClick={() => zetSoort('gelukt')}>{t('Geslaagd')}</button>
            </span></header>
          <Heatmap cellen={d.ritme.map((r) => ({ dag: r.dag, uur: r.uur, n: r[soort] }))} kleur={soort === 'mislukt' ? 'var(--st-serious)' : 'var(--cyan-ink)'}
            eenheid={soort === 'mislukt' ? t('mislukte logins') : t('geslaagde logins')} label={t('Logins per dag van de week en uur')} />
        </section>
        <div className="stack">
          <section className="card"><header><h2>{t('Landen')}</h2><span className="meta">{t('naar logins')}</span></header>
            <Balken rijen={d.landen.map((l) => ({ naam: land(l.cc), voor: <Vlag cc={l.cc} />, n: l.gelukt + l.mislukt,
              extra: l.mislukt ? t('{n} mislukt', { n: getal(l.mislukt) }) : null }))} /></section>
          <section className="card"><header><h2>{t('AnyConnect in gebruik')}</h2><span className="meta">{t('gebruikers, 30 dagen')}</span></header>
            <Balken rijen={d.versies.map((v) => ({ naam: `${v.versie} · ${PLATFORM[v.platform] ?? v.platform}`, n: v.gebruikers }))} /></section>
        </div>
      </div>
    </>
  );
}


/**
 * AnyConnect en Secure Client (docs/besluiten/anyconnect-versies.md): elke versie die gebruikers draaien (de laatste 30
 * dagen) of die op de FMC staat, met de kwetsbaarheden uit NVD; actief misbruikt (CISA KEV) in rood.
 */
function AnyConnect({ klant }) {
  const q = useQuery({ queryKey: ['anyconnect', klant], queryFn: () => api.anyconnect(klant), refetchInterval: 60_000 });
  return (
    <section className="card" style={{ marginTop: 18 }}>
      <header><h2>{t('AnyConnect-versies')}</h2><span className="meta">{t('in gebruik (30 dagen) en op de FMC, naast NVD en CISA KEV')}</span></header>
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : q.data.data.length ? (
        <AnyConnectVersies lijst={q.data.data} metKlant={klant === 'alle'} />
      ) : <p className="empty">{t('Nog geen AnyConnect-versies gezien. ADTS ZERO kijkt elke zes uur, in de syslog van VPN-logins en op de FMC.')}</p>}
      <p className="noot">{t('Een versie op de FMC is wat de firewall aan clients uitrolt; in gebruik is wat gebruikers echt draaien (uit de VPN-logins). Rood: actief misbruikt.')}</p>
    </section>
  );
}
